import React, { useEffect, useState } from 'react';
import { BackHandler, StyleSheet, View } from 'react-native';
import { useAuth } from '../../contexts/AuthContext';
import SupportScreen from '../SupportScreen';
import { V2SupportClientSummary } from '../../types/v2';
import SupportChooserScreen from './SupportChooserScreen';
import SupportClientListScreen from './SupportClientListScreen';
import SupportClientChatScreen from './SupportClientChatScreen';
import SupportHeader from './SupportHeader';

export type SupportInitialRoute = 'chooser' | 'personal';
type SupportRoute = SupportInitialRoute | 'clients' | 'clientChat';

interface Props {
  initialRoute?: SupportInitialRoute;
  onNavigate: (screen: string) => void;
}

const SupportFlowScreen: React.FC<Props> = ({ initialRoute = 'chooser', onNavigate }) => {
  const { user } = useAuth();
  const directSuperUser = user?.is_super_user === true && !user?.is_impersonating;
  const [route, setRoute] = useState<SupportRoute>(initialRoute);
  const [client, setClient] = useState<V2SupportClientSummary | null>(null);

  useEffect(() => {
    setRoute(initialRoute);
    setClient(null);
  }, [initialRoute]);

  useEffect(() => {
    if (!directSuperUser) return;
    const subscription = BackHandler.addEventListener('hardwareBackPress', () => {
      if (route === 'clientChat') {
        setRoute('clients');
        return true;
      }
      if (route === 'clients' || route === 'personal') {
        setClient(null);
        setRoute('chooser');
        return true;
      }
      return false;
    });
    return () => subscription.remove();
  }, [directSuperUser, route]);

  if (!directSuperUser) {
    return <SupportScreen onNavigate={onNavigate} />;
  }

  const navigate = (screen: string) => {
    if (screen === 'support') {
      setClient(null);
      setRoute('chooser');
      return;
    }
    onNavigate(screen);
  };

  if (route === 'personal') {
    return (
      <View style={styles.container}>
        <SupportHeader title="Personal support" onBack={() => setRoute('chooser')} />
        <SupportScreen hideHeader onNavigate={navigate} />
      </View>
    );
  }

  if (route === 'clients') {
    return (
      <SupportClientListScreen
        onBack={() => setRoute('chooser')}
        onClient={selected => { setClient(selected); setRoute('clientChat'); }}
        onNavigate={navigate}
      />
    );
  }

  if (route === 'clientChat' && client) {
    return (
      <SupportClientChatScreen
        client={client}
        onBack={() => setRoute('clients')}
        onNavigate={navigate}
      />
    );
  }

  return (
    <SupportChooserScreen
      onPersonal={() => setRoute('personal')}
      onClients={() => setRoute('clients')}
      onNavigate={navigate}
    />
  );
};

const styles = StyleSheet.create({ container: { flex: 1 } });

export default SupportFlowScreen;
